import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Chart, registerables } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { DepartmentType } from '../../models/request.model';
import { AnalyticsPeriod, AnalyticsQuery, SnapshotResponse, DistributionResponse, TrendsResponse, GeoResponse, PerformanceResponse } from '../../models/analytics.model';
import { RequestAnalyticsService } from '../../services/request-analytics.service';
import { RequestsV2ImplService } from '../../services/requests-v2.impl.service';

Chart.register(...registerables, annotationPlugin);

@Component({
    selector: 'app-request-analytics',
    templateUrl: './request-analytics.component.html',
    styleUrls: ['./request-analytics.component.scss'],
})
export class RequestAnalyticsComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    selectedPeriod: AnalyticsPeriod = '30d';
    comparePrevious = false;
    customFrom: string | null = null;
    customTo: string | null = null;
    showCustomDates = false;

    loading = false;
    error = false;

    snapshot: SnapshotResponse | null = null;
    distribution: DistributionResponse | null = null;
    trends: TrendsResponse | null = null;
    geo: GeoResponse | null = null;
    performance: PerformanceResponse | null = null;
    categoryColorMap: Record<string, string> = {};
    categoryList: { id: string; name: string; color: string }[] = [];

    departments: DepartmentType[] = [];
    selectedDepartments: string[] = [];

    periods: { value: AnalyticsPeriod; labelKey: string }[] = [
        { value: '7d', labelKey: 'request_v2.analytics.period_7d' },
        { value: '30d', labelKey: 'request_v2.analytics.period_30d' },
        { value: '90d', labelKey: 'request_v2.analytics.period_90d' },
        { value: 'custom', labelKey: 'request_v2.analytics.period_custom' },
    ];

    constructor(
        private analyticsService: RequestAnalyticsService,
        private requestsService: RequestsV2ImplService,
        private translate: TranslateService,
    ) {}

    ngOnInit(): void {
        this.loadDepartments();
        this.loadCategoryColors();
        this.loadData();

        this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.loadData();
        });
    }

    private loadDepartments(): void {
        this.requestsService
            .getDepartments()
            .pipe(takeUntil(this.destroy$))
            .subscribe((depts) => {
                this.departments = depts || [];
            });
    }

    onDepartmentChange(departmentIds: string[]): void {
        this.selectedDepartments = departmentIds;
        this.loadData();
    }

    private loadCategoryColors(): void {
        this.requestsService
            .getRequestCategories()
            .pipe(takeUntil(this.destroy$))
            .subscribe((categories) => {
                const map: Record<string, string> = {};
                const list: { id: string; name: string; color: string }[] = [];
                categories.forEach((cat: any) => {
                    const id = cat._id || cat.id;
                    if (id && cat.color) {
                        map[id] = cat.color;
                    }
                    if (id) {
                        list.push({ id, name: cat.name, color: cat.color || '#666' });
                    }
                });
                this.categoryColorMap = map;
                this.categoryList = list;
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onPeriodChange(period: AnalyticsPeriod): void {
        this.selectedPeriod = period;
        this.showCustomDates = period === 'custom';

        if (period !== 'custom') {
            this.customFrom = null;
            this.customTo = null;
            this.loadData();
        }
    }

    onCompareToggle(): void {
        this.comparePrevious = !this.comparePrevious;
        this.loadData();
    }

    onCustomDateChange(): void {
        if (this.customFrom && this.customTo) {
            this.loadData();
        }
    }

    retry(): void {
        this.loadData();
    }

    loadData(): void {
        if (this.selectedPeriod === 'custom' && (!this.customFrom || !this.customTo)) {
            return;
        }

        const query: AnalyticsQuery = {
            period: this.selectedPeriod,
            comparePrevious: this.comparePrevious,
        };

        if (this.selectedPeriod === 'custom') {
            query.from = this.customFrom;
            query.to = this.customTo;
        }

        if (this.selectedDepartments.length > 0) {
            query.departments = this.selectedDepartments;
        }

        this.loading = true;
        this.error = false;

        forkJoin({
            snapshot: this.analyticsService.getSnapshot(query),
            distribution: this.analyticsService.getDistribution(query),
            trends: this.analyticsService.getTrends(query),
            geo: this.analyticsService.getGeo(query),
            performance: this.analyticsService.getPerformance(query),
        })
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.loading = false;
                }),
            )
            .subscribe({
                next: (result) => {
                    this.snapshot = result.snapshot;
                    this.distribution = result.distribution;
                    this.trends = result.trends;
                    this.geo = result.geo;
                    this.performance = result.performance;
                },
                error: () => {
                    this.error = true;
                    this.snapshot = null;
                    this.distribution = null;
                    this.trends = null;
                    this.geo = null;
                    this.performance = null;
                },
            });
    }
}
